import { useTranslation } from 'react-i18next';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { ORDER_METHODS, REDUX_STORE_SLICES } from '../../services/exports/Constants';
import moment from 'moment';
import useOrderMethodsStatus from '../../hooks/availability/useOrderMethodsStatus';
import useCompanyLocation from '../../hooks/location/useCompanyLocation';
import HelperMethods from '../../services/exports/HelperMethods';
import useOrderPreparationTime from '../../hooks/order/useOrderPreparationTime';
import AddressHelpers from '../../services/helpers/AddressHelpers';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import classnames from 'classnames';
import { FC, ReactElement, useMemo, useState } from 'react';
import useOpeningStatus from '../../hooks/availability/useOpeningStatus';
import CompanyDetailsModal from '../../modals/CompanyDetailsModal';
import useStore from '../../hooks/store/useStore';

interface Props {
  className?: string;
  type?: 1 | 2;
}

type InfoRowProps = {
  withChangeButton?: boolean;
  className?: string;
};

const OrderType: FC<Props> = ({ className, type }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:OrderType:OrderType' });

  const { localData } = useOrderFlow();
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const getPreparationTime = useOrderPreparationTime(company);
  const { formatDateTime, createScheduledOrderSlot, toggleOrderMethodModal } = useAppMethods();
  const { isOrderMethodCurrentlyOpen, isOrderMethodOpenForFutureOrders } = useOrderMethodsStatus();
  const openingStatus = useOpeningStatus();
  const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState<boolean>(false);

  const location = useCompanyLocation(localData.scheduled_for === null ? null : localData.scheduled_for);

  const getScheduledTime = (): string => {
    if (localData.scheduled_for === null) {
      return t('asap');
    }

    return moment(localData.scheduled_for).isSame(moment(), 'day')
      ? createScheduledOrderSlot(localData.scheduled_for)
      : formatDateTime(localData.scheduled_for, { format: 'dd, DD MMM HH:mm' });
  };

  const renderPreparationTime = (method: ORDER_METHODS) => {
    const isMethodAvailable = isOrderMethodCurrentlyOpen(method) || isOrderMethodOpenForFutureOrders(method);

    if (!isMethodAvailable) {
      return t('unavailable');
    }

    if (localData.scheduled_for) {
      return getScheduledTime();
    }

    return t('time', {
      time: HelperMethods.formatTimeInterval(getPreparationTime(method)),
    });
  };

  const getAddress = (): string => {
    if (localData.method === ORDER_METHODS.DELIVERY) {
      return AddressHelpers.setCountry(location?.country).format({
        street_name: localData?.delivery_street_name,
        city: localData?.delivery_city,
        zip_code: localData?.delivery_zip_code,
      });
    }

    return AddressHelpers.setCountry(location?.country).format({
      street_name: location?.address,
      city: location?.city,
      zip_code: location?.zip_code,
    });
  };

  const OrderMethod = ({ withChangeButton, className }: InfoRowProps): ReactElement => {
    return (
      <div className="flex justify-between">
        <div className={classnames('flex divide-x divide-gray text-sm', className)}>
          <div className="pr-2">
            <span>{t(`order_methods.${localData.method}`)}</span>
          </div>
          {!localData?.scheduled_for && (
            <div className="px-2 pl-2">
              <span>ASAP</span>
            </div>
          )}
          <div className="px-2 pl-2">
            <span>{renderPreparationTime(localData.method)}</span>
          </div>
        </div>
        {withChangeButton && (
          <button
            className="text-brand-primary text-xs"
            onClick={() => toggleOrderMethodModal({ openModal: true, orderMethod: localData.method })}
          >
            {t('buttons.change')}
          </button>
        )}
      </div>
    );
  };

  const CompanyName = ({ className }: InfoRowProps): ReactElement => {
    return (
      <div className="flex justify-between gap-sm items-start text-xs w-full">
        <div>
          <p className={classnames('text-sm', className)}>{getAddress()}</p>
          <p className={classnames('text-sm', className)}>{company?.name}</p>
        </div>
      </div>
    );
  };

  const renderCompanyDetailsModal = useMemo<JSX.Element>(() => {
    return (
      <CompanyDetailsModal
        data={company}
        openModal={showCompanyDetailsModal}
        toggleModal={() => setShowCompanyDetailsModal(false)}
      />
    );
  }, [company, showCompanyDetailsModal]);

  if (type === 2) {
    return (
      <>
        <div className={classnames('border border-gray px-md rounded-lg divide-y divide-gray', className)}>
          <div className="flex justify-between py-sm gap-x-sm">
            <div>
              <OrderMethod className="text-sm font-normal text-gray-700" />
              <CompanyName className="text-sm font-normal text-gray-700" />
            </div>
            <button
              className="underline text-sm h-fit"
              onClick={() => toggleOrderMethodModal({ openModal: true, orderMethod: localData.method })}
            >
              {t('buttons.change')}
            </button>
          </div>
          <div className="flex justify-between py-sm gap-x-sm">
            <p className="text-sm font-normal text-gray-700">{openingStatus.details_text}</p>
            <button className="underline text-sm" onClick={() => setShowCompanyDetailsModal(true)}>
              {t('buttons.see_hours')}
            </button>
          </div>
        </div>
        {renderCompanyDetailsModal}
      </>
    );
  }

  return (
    <div className={classnames('border border-gray space-y-sm p-sm rounded-lg', className)}>
      <OrderMethod withChangeButton />
      <CompanyName />
    </div>
  );
};

export default OrderType;
